import React, { useEffect, useState } from "react";
import { config } from "../../config";

const PLAYLIST_STORAGE_KEY = "ndtv-last-playlist-order-v1";

const getVideoTime = (video) => {
    const rawDate = video?.date_ajout
        || video?.created_at
        || video?.date_creation
        || "";
    const timestamp = rawDate ? new Date(rawDate).getTime() : 0;
    return Number.isNaN(timestamp) ? 0 : timestamp;
};

const playlistSignature = (items) => items
    .map((video) => String(video.id || video.fichier))
    .join("|");

const shufflePlaylistForVisit = (items) => {
    const sorted = [...items].sort((a, b) => {
        const dateDiff = getVideoTime(b) - getVideoTime(a);
        if (dateDiff !== 0) return dateDiff;
        return Number(b.id || 0) - Number(a.id || 0);
    });

    if (sorted.length <= 1) return sorted;

    const previousSignature = localStorage.getItem(PLAYLIST_STORAGE_KEY);
    let shuffled = sorted;

    for (let attempt = 0; attempt < 8; attempt += 1) {
        shuffled = [...sorted];
        for (let i = shuffled.length - 1; i > 0; i -= 1) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        if (playlistSignature(shuffled) !== previousSignature) break;
    }

    if (playlistSignature(shuffled) === previousSignature) {
        shuffled = [...sorted.slice(1), sorted[0]];
    }

    localStorage.setItem(PLAYLIST_STORAGE_KEY, playlistSignature(shuffled));
    return shuffled;
};

export default function TVSahel() {
    // Utilisation de la configuration centralisée (PHP uniquement)
    const API_BASE = config.apiBase;
    const VIDEO_BASE_URL = `${config.uploadsBase}/videos`;

    const [videos, setVideos] = useState([]);
    const [index, setIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [reload, setReload] = useState(0);

    const fetchVideos = async (signal) => {
        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error('Connectez-vous pour regarder TV Sahel.');
            const res = await fetch(`${API_BASE}/get_videos.php`, {
                headers: { Authorization: `Bearer ${token}` },
                cache: 'no-store', signal
            });
            const data = await res.json();
            if (!res.ok || data.success === false) throw new Error(data.message || 'Les vidéos sont momentanément indisponibles.');
            const incomingVideos = Array.isArray(data.videos)
                ? data.videos
                : [];
            setVideos(shufflePlaylistForVisit(incomingVideos));
            setIndex(0);
        } catch (err) {
            if (err.name === 'AbortError') return;
            setError(err.message || 'Impossible de charger les vidéos. Réessayez.');
            setVideos([]);
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    };

    useEffect(() => {
        const controller = new AbortController();
        fetchVideos(controller.signal);
        return () => controller.abort();
    }, [reload]);

    const nextVideo = () => {
        setIndex((prev) => {
            if (videos.length === 0) return 0;
            return (prev + 1) % videos.length;
        });
    };

    if (loading) {
        return (
            <div className="flex min-h-screen w-full items-center justify-center bg-black text-white">
                <div className="text-center opacity-70">
                    <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white/70"></div>
                    <h1 className="text-sm font-bold tracking-wide text-white/70">
                        NDTV
                    </h1>
                    <p className="mt-1 text-[9px] uppercase tracking-[0.25em] text-white/35">
                        Chargement
                    </p>
                </div>
            </div>
        );
    }

    if (videos.length === 0) {
        return (
            <div className="flex min-h-screen w-full items-center justify-center bg-black text-white">
                <div className="text-center opacity-70">
                    <h1 className="text-lg font-bold tracking-wide text-white/75">
                        NDTV
                    </h1>
                    <p className="mt-2 text-xs text-white/45">
                        {error || 'Aucune vidéo disponible'}
                    </p>
                    {error && <button type="button" onClick={() => setReload(v => v + 1)} className="mt-5 rounded-lg border border-emerald-500/50 px-5 py-2 text-sm text-white">Réessayer</button>}
                </div>
            </div>
        );
    }

    const current = videos[index];
    const videoUrl = `${VIDEO_BASE_URL}/${current.fichier}`;

    return (
        <div className="min-h-screen w-full bg-black text-white">
            <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-black">

                {/* VIDEO */}
                <video
                    key={current.id}
                    autoPlay
                    controls
                    playsInline
                    onEnded={nextVideo}
                    className="
                        w-full bg-black object-contain
                        aspect-video
                        md:absolute md:inset-0 md:h-full md:w-full md:object-cover md:aspect-auto
                    "
                >
                    <source src={videoUrl} type="video/mp4" />
                    Votre navigateur ne peut pas lire cette vidéo.
                </video>

                {/* LOGO NDTV PETIT */}
                <div className="pointer-events-none absolute left-2 top-2 z-20 bg-black/25 px-2 py-1 text-[10px] font-bold tracking-wide text-white/55 backdrop-blur-sm">
                    NDTV
                </div>

                {/* COMPTEUR PETIT */}
                <div className="pointer-events-none absolute right-2 top-2 z-20 bg-black/20 px-2 py-1 text-[9px] font-semibold text-white/45 backdrop-blur-sm">
                    {index + 1}/{videos.length}
                </div>

                {/* TITRE PETIT */}
                <div className="pointer-events-none absolute bottom-2 left-2 z-20 max-w-[75vw] truncate bg-black/20 px-2 py-1 text-[10px] font-medium text-white/55 backdrop-blur-sm md:text-xs">
                    {current.titre}
                </div>

            </div>
        </div>
    );
}
